/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { StandaloneOnlineMergeModalsType, PhoneOTPViews } from '@/features/mergeRewards';
import type { CompletedAutoCreatedViews } from '@/features/mergeRewards/components/CompletedAutoCreatedAccountModal/interface';
import { useGlobalState } from '@/hooks/useGlobalState';
import { type LoginConfirmationState } from '@/types/globalState';
import { setCookie } from '@/utils/cookie';
import useLatestCallback from 'use-latest-callback';

export type LoginConfirmationAction =
  | {
      type: 'SHOW_REWARD_CONFIRMATION';
      payload: {
        rewardsProfileInfo?: LoginConfirmationState['rewardsProfileInfo'];
      };
    }
  | {
      type: 'SHOW_CREATE_ACCOUNT_CONFIRMATION';
    }
  | {
      type: 'SHOW_REWARD_SIGNIN';
      payload: {
        view?: StandaloneOnlineMergeModalsType;
      };
    }
  | {
      type: 'VALIDATE_REWARD_QUESTIONS';
      payload: {
        isRegisterFlow?: boolean;
        rewardsProfileInfo?: LoginConfirmationState['rewardsProfileInfo'];
      };
    }
  | {
      type: 'HIDE_POPUP';
    }
  | {
      type: 'PHONE_OTP_VALIDATION';
      payload: {
        view?: PhoneOTPViews;
      };
    }
  | {
      type: 'COMPLETE_AUTO_CREATED_ACCOUNT';
      payload: {
        view?: CompletedAutoCreatedViews;
      };
    };

export const loginConfirmationReducer = (
  state: LoginConfirmationState,
  action: LoginConfirmationAction,
): LoginConfirmationState => {
  switch (action.type) {
    case 'SHOW_REWARD_CONFIRMATION': {
      const { rewardsProfileInfo } = action.payload;
      return {
        ...state,
        modalType: { flow: 'SHOW_REWARD_CONFIRMATION', view: 'WELCOME' },

        rewardsProfileInfo: rewardsProfileInfo
          ? {
              ...rewardsProfileInfo,
            }
          : state.rewardsProfileInfo,
      };
    }

    case 'SHOW_CREATE_ACCOUNT_CONFIRMATION': {
      return {
        ...state,
        modalType: { flow: 'SHOW_CREATE_ACCOUNT_CONFIRMATION', view: 'WELCOME' },
      };
    }

    case 'SHOW_REWARD_SIGNIN': {
      return {
        ...state,
        modalType: {
          flow: 'SHOW_REWARD_SIGNIN',
          view: action.payload.view ?? 'SHOW_REWARD_SIGNIN',
        },
      };
    }

    case 'VALIDATE_REWARD_QUESTIONS': {
      const { isRegisterFlow, rewardsProfileInfo } = action.payload;
      return {
        ...state,
        modalType: { flow: 'VALIDATE_REWARD_QUESTIONS', view: 'WELCOME' },
        isRegisterFlow: isRegisterFlow ?? state.isRegisterFlow,
        rewardsProfileInfo: rewardsProfileInfo
          ? {
              ...rewardsProfileInfo,
            }
          : state.rewardsProfileInfo,
      };
    }

    case 'PHONE_OTP_VALIDATION': {
      return {
        ...state,
        modalType: { flow: 'PHONE_OTP_VALIDATION', view: action.payload.view ?? 'SEND_CODE' },
      };
    }

    case 'COMPLETE_AUTO_CREATED_ACCOUNT': {
      return {
        ...state,
        modalType: {
          flow: 'COMPLETE_AUTO_CREATED_ACCOUNT',
          view: action.payload.view ?? 'WELCOME',
        },
      };
    }

    case 'HIDE_POPUP': {
      return {
        ...state,
        modalType: null,
      };
    }

    default: {
      return state;
    }
  }
};

export const useLoginConfirmation = () => {
  const [loginConfirmationState, setGlobalState] = useGlobalState('loginConfirmation');

  const dispatch = useLatestCallback((action: LoginConfirmationAction) => {
    const newState = loginConfirmationReducer(loginConfirmationState, action);
    setGlobalState(newState);
  });

  const hideConfirmationPopup = useLatestCallback(() => {
    dispatch({ type: 'HIDE_POPUP' });
    void setCookie('AZ_SIGNED_IN', 'true');
  });

  const showRewardSigninPopup = useLatestCallback((view?: StandaloneOnlineMergeModalsType) => {
    dispatch({ type: 'SHOW_REWARD_SIGNIN', payload: { view } });
  });

  return {
    loginConfirmationState,
    hideConfirmationPopup,
    showRewardSigninPopup,
    changePopupType: dispatch,
  } as const;
};
